import { SIGNING_CONTRACT_HASH_KEY } from '@/env';
import { message } from 'antd';

export interface SigningContractInvestorPayload {
  fullName?: string;
  idNumber?: string;
  dob?: string;
  issueDate?: string;
  issuePlace?: string;
  email?: string;
  phone?: string;
  address?: string;
  bankAccount?: string;
  bank?: string;
}

export interface SigningContractPayload {
  packageId: number;
  contractInfo?: {
    name?: string;
    price?: number;
  };
  contractData?: {
    contractNumber?: string;
    signDate?: string;
    depositDate?: string;
    finalPaymentDate?: string;
    investor?: SigningContractInvestorPayload;
  };
}

const toBase64Url = (input: string) => {
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const fromBase64Url = (input: string) => {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

  return new TextDecoder().decode(bytes);
};

const hmacSha256Hex = async (input: string, key: string) => {
  const subtle = window.crypto?.subtle;

  if (!subtle) {
    message.error('HTTPS is required for secure cryptographic operations. Please ensure you are accessing the application over HTTPS.');
    throw new Error('Web Crypto API is not available in this environment use secure environment to use signing contract features');
  }

  const cryptoKey = await subtle.importKey(
    'raw',
    new TextEncoder().encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await subtle.sign('HMAC', cryptoKey, new TextEncoder().encode(input));
  return Array.from(new Uint8Array(signature)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const encodeSigningContractData = async (
  payload: SigningContractPayload,
  key = SIGNING_CONTRACT_HASH_KEY
) => {
  const payloadBase64 = toBase64Url(JSON.stringify(payload));
  const signature = await hmacSha256Hex(payloadBase64, key);
  return `${payloadBase64}.${signature}`;
};

export const decodeSigningContractData = async (
  encodedData: string,
  key = SIGNING_CONTRACT_HASH_KEY
): Promise<SigningContractPayload> => {
  const parts = encodedData.split('.');
  if (parts.length !== 2) {
    throw new Error('Invalid signing contract payload format');
  }

  const [payloadBase64, incomingSignature] = parts;
  const expectedSignature = await hmacSha256Hex(payloadBase64, key);

  if (incomingSignature !== expectedSignature) {
    throw new Error('Invalid signing contract signature');
  }

  const decoded = fromBase64Url(payloadBase64);
  const parsed = JSON.parse(decoded) as SigningContractPayload;

  if (!parsed?.packageId || Number.isNaN(Number(parsed.packageId))) {
    throw new Error('Invalid signing contract payload: packageId is required');
  }

  return parsed;
};

export const generateSigningContractUrl = async (
  payload: SigningContractPayload,
  basePath = '/signingContract',
  key = SIGNING_CONTRACT_HASH_KEY
) => {
  const encoded = await encodeSigningContractData(payload, key);
  return `${basePath}?data=${encodeURIComponent(encoded)}`;
};

export const demoSigningContractPayload: SigningContractPayload = {
  packageId: 1,
  contractInfo: {
    name: 'TITAN',
    price: 15000000,
  },
  contractData: {
    signDate: '2026-02-24',
    depositDate: '2026-02-25',
    finalPaymentDate: '2026-03-25',
    investor: {
      fullName: 'NGUYEN VAN A',
      idNumber: '079123456789',
      dob: '1995-05-20',
      issueDate: '2022-01-10',
      issuePlace: 'Cục Cảnh sát QLHC về TTXH',
      email: 'nguyenvana@example.com',
      phone: '0909123456',
      address: '67 Đinh Thị Thi, TP.HCM',
      bankAccount: '0123456789',
      bank: 'Vietcombank',
    },
  },
};

export const getDemoSigningContractUrl = async () => generateSigningContractUrl(demoSigningContractPayload);
